<template>
    <div style="position:fixed;top:0px;left:0px;right:0px;bottom:0px; background: linear-gradient(160deg, #EEF2FF 0%, #F8FAFF 40%, #FFFFFF 100%); overflow-y: auto;">
    <!-- PDF Viewer Dialog -->
    <v-dialog v-model="showPdf" max-width="920">
        <v-card rounded="lg">
            <v-card-title class="d-flex justify-space-between align-center pa-3">
                <span class="text-subtitle-1 font-weight-medium">
                    <v-icon class="mr-1" color="indigo">mdi-file-pdf-box</v-icon>
                    Aperçu du document
                </span>
                <v-btn icon="mdi-close" variant="text" density="comfortable" @click="showPdf = false"></v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <object :data="filebase64" type="application/pdf" width="100%" style="height: 80vh;"></object>
        </v-card>
    </v-dialog>

    <!-- Main content -->
    <v-container class="d-flex justify-center" style="padding-top: 10vh; padding-bottom: 4vh;">
        <v-col cols="12" style="max-width: 620px;" class="pa-0">

            <!-- Loading skeleton -->
            <v-card v-if="!signatureData && pageIsValide" class="pa-6" rounded="lg" elevation="3">
                <v-skeleton-loader type="heading, list-item-avatar, list-item-avatar, list-item-avatar, button, divider, paragraph, button"></v-skeleton-loader>
            </v-card>

            <!-- Invalid page -->
            <v-card v-else-if="!pageIsValide" class="pa-8 text-center" rounded="lg" elevation="3">
                <v-icon size="64" color="red-lighten-1">mdi-alert-circle-outline</v-icon>
                <div class="text-h6 mt-4 font-weight-bold">Lien invalide ou expiré</div>
                <div class="text-body-2 text-medium-emphasis mt-2">
                    Ce lien de signature est introuvable ou n'est plus valide.
                </div>
            </v-card>

            <!-- Main card -->
            <v-card v-else rounded="lg" elevation="3">

                <!-- Header -->
                <div class="header-gradient pa-5 rounded-t-lg">
                    <div class="d-flex align-center gap-3">
                        <v-avatar color="white" size="38" class="mr-3">
                            <v-icon color="indigo-darken-3" size="22">mdi-pen</v-icon>
                        </v-avatar>
                        <div>
                            <div class="text-h6 font-weight-bold text-white">Signature électronique</div>
                            <div class="text-caption" style="color: rgba(255,255,255,0.8);">
                                Double authentification sécurisée
                            </div>
                        </div>
                    </div>
                </div>

                <div class="pa-5">

                    <!-- Signataire info card -->
                    <v-card variant="tonal" color="indigo" rounded="lg" class="mb-5 pa-4">
                        <div class="text-overline text-indigo-darken-2 mb-3" style="font-weight: 700; letter-spacing: 1.5px;">
                            Informations du signataire
                        </div>
                        <div class="d-flex flex-column" style="gap: 10px;">
                            <div class="d-flex align-center gap-2">
                                <v-icon size="18" color="indigo-darken-2">mdi-account-outline</v-icon>
                                <span class="font-weight-medium">{{ signatureData.CLIENT }}</span>
                            </div>
                            <div class="d-flex align-center gap-2">
                                <v-icon size="18" color="indigo-darken-2">mdi-email-outline</v-icon>
                                <span class="text-body-2">{{ signatureData.EMAIL }}</span>
                            </div>
                            <div class="d-flex align-center gap-2">
                                <v-icon size="18" color="indigo-darken-2">mdi-phone-outline</v-icon>
                                <span class="text-body-2">{{ signatureData.TEL }}</span>
                            </div>
                        </div>
                        <v-btn
                            variant="outlined"
                            color="indigo-darken-2"
                            size="small"
                            prepend-icon="mdi-file-pdf-box"
                            rounded="lg"
                            class="mt-4"
                            @click="showPdf = true"
                        >
                            Visualiser le document
                        </v-btn>
                    </v-card>

                    <!-- Signature flow -->
                    <template v-if="!isSigned">

                        <!-- Step 1 -->
                        <div class="mb-4">
                            <div class="d-flex align-center gap-2 mb-3">
                                <v-avatar :color="showCodeForm ? 'green' : 'indigo'" size="26">
                                    <v-icon v-if="showCodeForm" size="14" color="white">mdi-check</v-icon>
                                    <span v-else class="text-white font-weight-bold" style="font-size: 12px;">1</span>
                                </v-avatar>
                                <span class="ml-2 my-3 text-body-2 font-weight-medium text-medium-emphasis">
                                    {{ showCodeForm ? 'Code envoyé par mail' : 'Recevoir un code de vérification' }}
                                </span>
                            </div>

                            <v-btn
                                v-if="!showCodeForm"
                                block
                                color="green-darken-1"
                                size="large"
                                prepend-icon="mdi-email-fast-outline"
                                rounded="lg"
                                style="text-transform: none;"
                                :loading="validationIsLoading"
                                class="btn-sign"
                                @click="sendSMS"
                            >
                                Recevoir le code sur {{ signatureData.EMAIL }}
                            </v-btn>

                            <v-btn
                                v-else
                                block
                                variant="outlined"
                                color="green-darken-1"
                                size="small"
                                prepend-icon="mdi-refresh"
                                rounded="lg"
                                :loading="validationIsLoading"
                                @click="sendSMS"
                            >
                                Renvoyer un nouveau code
                            </v-btn>
                        </div>

                        <!-- Step 2 -->
                        <v-expand-transition>
                            <div v-if="showCodeForm">
                                <v-divider class="mb-4"></v-divider>
                                <div class="d-flex align-center gap-2 mb-3">
                                    <v-avatar color="indigo" size="26">
                                        <span class="text-white font-weight-bold" style="font-size: 12px;">2</span>
                                    </v-avatar>
                                    <span class="text-body-2 font-weight-medium text-medium-emphasis">Saisir le code reçu</span>
                                </div>
                                <v-card variant="tonal" color="green" rounded="lg" class="pa-4">
                                    <v-text-field
                                        v-model="tmpCode"
                                        type="number"
                                        label="Code reçu par mail"
                                        placeholder="Ex : 123456"
                                        bg-color="white"
                                        variant="outlined"
                                        density="comfortable"
                                        prepend-inner-icon="mdi-shield-key-outline"
                                        hide-details="auto"
                                        rounded="lg"
                                        autofocus
                                        @keyup.enter="verifCodeIsValid"
                                    ></v-text-field>
                                    <v-btn
                                        block
                                        color="green-darken-1"
                                        size="large"
                                        prepend-icon="mdi-check-decagram"
                                        rounded="lg"
                                        class="btn-sign mt-3"
                                        :loading="validationIsLoading"
                                        :disabled="!tmpCode"
                                        @click="verifCodeIsValid"
                                    >
                                        Valider ma signature
                                    </v-btn>
                                </v-card>
                            </div>
                        </v-expand-transition>
                    </template>

                    <!-- Success state -->
                    <v-card v-else color="green-lighten-5" rounded="lg" class="pa-6 text-center">
                        <v-icon size="72" color="green">mdi-check-circle</v-icon>
                        <div class="text-h6 font-weight-bold text-green-darken-2 mt-3">
                            Document signé avec succès
                        </div>
                        <div class="text-body-2 text-medium-emphasis mt-1">
                            Votre signature électronique a été validée et enregistrée.
                        </div>
                        <v-btn
                            color="indigo"
                            variant="tonal"
                            prepend-icon="mdi-file-pdf-box"
                            rounded="lg"
                            class="mt-4"
                            @click="showPdf = true"
                        >
                            Visualiser le document signé
                        </v-btn>
                    </v-card>

                </div>
            </v-card>
        </v-col>
    </v-container>

    <!-- Snackbar -->
    <v-snackbar
        v-model="errorSnackBar.show"
        :color="errorSnackBar.color"
        location="top"
        rounded="lg"
        :timeout="4000"
    >
        <div class="d-flex align-center gap-2">
            <v-icon>{{ errorSnackBar.color === 'green' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
            <span class="font-weight-medium">{{ errorSnackBar.label }}</span>
        </div>
    </v-snackbar>
    </div>
</template>

<script>

import engine from '@/projects/pdf-signer/core/index'
import moment from 'moment'

const Buffer = require('buffer').Buffer
const { PDFDocument } = require('pdf-lib');

export default {
    data() {
        return {
            isSigned: false,
            errorSnackBar: {
                show: false,
                color: undefined,
                label: undefined,
            },
            showCodeForm: false,
            tmpCode: undefined,
            validationIsLoading: false,
            showPdf: false,
            pageIsValide: true,
            id: undefined,
            filebase64: undefined,
            signatureData: undefined,
            newFileSigned: undefined,
        }
    },
    methods: {
        verifCodeIsValid() {
            let that = this
            this.validationIsLoading = true
            engine.get_double_authentification_data_from_id(this.id, function (data) {
                if (data.code == 0) {
                    let info = data.data.data[0].fieldData
                    if (info.CODE == that.tmpCode) {
                        that.errorSnackBar = {
                            color: 'green',
                            label: 'Votre signature est validée',
                            show: true,
                        }
                        that.addSignatureInFile()
                        that.isSigned = true
                        that.validationIsLoading = false
                    } else {
                        that.errorSnackBar = {
                            color: 'red',
                            label: 'Le code renseigné est incorrect',
                            show: true,
                        }
                        that.validationIsLoading = false
                    }
                } else {
                    that.errorSnackBar = {
                        color: 'red',
                        label: 'Le code renseigné est incorrect',
                        show: true,
                    }
                    that.validationIsLoading = false
                }
            })
        },
        async addSignatureInFile() {
            let pdfDoc = await PDFDocument.load(this.filebase64);
            let info = this.signatureData.FILE_PATH.split('/')
            info = info[info.length - 1].split('@@')

            let obj = {
                resaNum: info[1],
                typeValue: info[0],
                title: info[1],
                date: info[2],
                isSigned: info[3],
                position: info[4].split('-'),
                client: info[5]
            }

            let page = pdfDoc.getPages()[0];
            let posX = parseFloat(obj.position[0])
            let posY = parseFloat(obj.position[1])

            let font = await pdfDoc.embedFont('Helvetica');

            page.drawText(obj.client, {
                x: posX + 10,
                y: posY + 45,
                size: 12,
                font,
            });

            page.drawText('Signée le ' + moment().format('DD/MM/YYYY HH:mm'), {
                x: posX + 50,
                y: posY + 15,
                size: 10,
                font,
            });

            const pdfBytes = await pdfDoc.save();
            const pdfUint8Array = new Uint8Array(pdfBytes);
            const base64String = "data:application/pdf;base64," + Buffer.from(pdfUint8Array).toString('base64');
            this.newFileSigned = base64String
            this.filebase64 = base64String

            engine.signe_and_change_file_name(
                this.signatureData,
                this.signatureData.FILE_PATH,
                this.newFileSigned,
                function () { }
            )
        },
        sendSMS() {
            let that = this
            this.showCodeForm = true
            this.validationIsLoading = true
            engine.send_sms_double_authent(this.id, this.signatureData.EMAIL, this.signatureData.TEL, function () {
                that.validationIsLoading = false
            })
        }
    },
    mounted() {
        let that = this
        if (this.$route.query && this.$route.query.id) {
            this.id = this.$route.query.id
            engine.get_double_authentification_data_from_id(this.id, function (data) {
                if (data.code == 0) {
                    that.signatureData = data.data.data[0].fieldData
                    engine.get_file_from_path(that.signatureData.FILE_PATH, function (data2) {
                        that.filebase64 = "data:application/pdf;base64," + new Buffer.from(data2.data.fileBinary).toString('base64');
                    })
                } else {
                    that.pageIsValide = false
                }
            })
        } else {
            this.pageIsValide = false
        }
    }
}
</script>

<style scoped>
.header-gradient {
    background: linear-gradient(135deg, #3949AB, #5C6BC0);
}

.btn-sign {
    font-weight: bold;
    letter-spacing: 0.5px;
}
</style>
